import React from 'react'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

import { useDropdownContext } from '../DropdownContext'
import S from './Content.styled'

const Section = ({ children }) => {
  const { toggle } = useDropdownContext()
  const sectionRef = useRef(null)
  const cachedElementsRef = useRef(null)

  useEffect(() => {
    cachedElementsRef.current = null
  }, [children])

  const getFocusableElements = () => {
    if (cachedElementsRef.current) {
      return cachedElementsRef.current
    }

    if (!sectionRef.current) return []

    const dropdownContainer = sectionRef.current

    const getRadioGroups = () => {
      const groups = new Map()
      dropdownContainer
        .querySelectorAll('input[type="radio"]:not([disabled])')
        .forEach((radio) => {
          if (!groups.has(radio.name)) {
            const checked = dropdownContainer.querySelector(
              `input[type="radio"][name="${radio.name}"]:checked`
            )
            groups.set(radio.name, checked || radio)
          }
        })
      return [...groups.values()]
    }

    const elements = [
      ...dropdownContainer.querySelectorAll(
        '[role="menuitem"]:not([aria-disabled="true"]), input[type="checkbox"]:not([disabled])'
      ),
      ...getRadioGroups(),
    ]

    cachedElementsRef.current = elements
    return elements
  }

  const handleKeyDown = (e) => {
    const focusableElements = getFocusableElements()

    if (focusableElements.length === 0) return

    const currentIndex = focusableElements.findIndex(
      (el) =>
        el === document.activeElement || el.contains(document.activeElement)
    )

    const isVoiceOver = e.ctrlKey && e.altKey
    const isAtFirst = currentIndex === 0
    const isAtLast = currentIndex === focusableElements.length - 1

    const navigate = (direction) => {
      const nextIndex =
        direction === 'forward' ? currentIndex + 1 : currentIndex - 1
      const isAtBoundary = direction === 'forward' ? isAtLast : isAtFirst

      if (isAtBoundary) {
        toggle()
        return
      }

      focusableElements[nextIndex]?.focus()
    }

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        if (!isVoiceOver && document.activeElement.type !== 'radio') {
          e.preventDefault()
          navigate(e.key === 'ArrowDown' ? 'forward' : 'backward')
        }
        break

      case 'ArrowLeft':
      case 'ArrowRight':
        if (isVoiceOver) {
          navigate(e.key === 'ArrowRight' ? 'forward' : 'backward')
        }
        break

      case 'Tab':
        if ((e.shiftKey && isAtFirst) || (!e.shiftKey && isAtLast)) {
          toggle()
        }
        break

      case 'Escape':
        e.preventDefault()
        toggle()
        break

      case 'Home':
        e.preventDefault()
        focusableElements[0]?.focus()
        break

      case 'End':
        e.preventDefault()
        focusableElements[focusableElements.length - 1]?.focus()
        break
    }
  }

  return (
    <S.ContentSection
      ref={sectionRef}
      as="fieldset"
      className="section"
      role="group"
      onKeyDown={(e) => handleKeyDown(e)}
    >
      {children}
    </S.ContentSection>
  )
}

Section.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Section
